import React, { useEffect, useState, useRef, useMemo } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton, Box, Typography, Grid } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { FormProvider, useForm } from 'react-hook-form';

import Purchase from './Purchase';
import Return from './Return';
import Accomodation from './Accomodation';
import DieselPurchase from './DieselPurchase';
import Recharge from './Recharge';

// Assuming FormInput/FormTextField exists in your FormControls for regular text inputs
import { DropzoneUpload, FormAutocomplete, FormSelect, FormInput } from './FormControls';
import { useToast } from 'context/tostifyContext';
import { toLocalISO } from 'routes/Pages/GenTask/components/SharedFormFields';
import { useAllUsers } from 'context/AllUserDataContext';
import useSPList from '../hooks/useSPList';
import useGetDropdownValues from 'routes/Pages/Dockets/CustomHooks/useGetDropdownValues';
import SummaryCard from 'routes/Pages/SiteAssessment/components/SummaryCard';
import customAxios from 'routes/Pages/util/Api';
import authHeader from 'services/auth/jwt/auth-header';

const DEFAULT = {
  poReqNumber: 0,
  submissionDate: toLocalISO(),
  poDate: toLocalISO(),
  purchaseOrReturn: '',
  requestorEmail: '',
  supplierName: '',
  emailToSupplier: '',
  originalRequisitionReference: '',
  emailAddresses: '',
  description: '',
  type: '',
  toolsAssignedTo: '',
  otherOption: '',
  warrantyPeriod: '',
  whPartRefNumber: '',
  supplierPartRefNumber: '',
  bookInDate: toLocalISO(),
  checkoutDate: toLocalISO(),
  totalDays: 0,
  ratePerNight: 0,
  totalNetValue: 0,
  whContractNumber: '',
  valueToBeRecharged: 0,
  rechargeDetailsForAdmin: '',
  customerName: '',
  whName: '',
  requisitionStatus: 'Requested',
  originatedBy: '',
  spName: '',
  fuelLocationDropPin: '',
  litresOrdered: 0,
  litresDelivered: 0,
  departmentMachine: '',
  department2Machine2: '',
  priorPORequisitionNumber: '',
  files: null,
  invoiceFiles: null,
  // Added default values for the two new fields
  purchaserFeedback: '',
  feedbackSentTo: '',
};

export const SwitchSections = (purchaseOrReturn, values) => {
  switch (purchaseOrReturn) {
    case 'Purchase':
      return <Purchase values={values} />;
    case 'Return':
      return <Return values={values} />;
    case 'Accommodation':
      return <Accomodation values={values} />;
    case 'Diesel Purchase':
      return <DieselPurchase values={values} />;
    case 'Recharge':
      return <Recharge values={values} />;
    default:
      return null;
  }
};

const AddPurchaseRequisitionForm = ({
  open,
  setOpen,
  isEdit,
  selectedPurchaseReq,
  handlePurchseReqSubmit,
  handleRfqEdit,
  handleOpenConfirmationDialog,
  uploadedDocs,
  handleFileDelete,
  viewFile,
  departmentList,
  supplierList,
}) => {
  console.log('selectedPurchaseReq', selectedPurchaseReq);
  const { showToast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [isSendingFeedback, setIsSendingFeedback] = useState(false);

  const { allUsers = [] } = useAllUsers();
  const { spList = [] } = useSPList();
  const machines = useGetDropdownValues('VehicleDepartmentMachine');
  const purchaseTypes = useGetDropdownValues('Type');
  const warrenties = useGetDropdownValues('WarrantyPeriod');

  const methods = useForm({
    defaultValues: DEFAULT,
    mode: 'onChange',
  });

  const { watch, reset, setValue, getValues, handleSubmit: hookFormSubmit } = methods;
  const purchaseOrReturn = watch('purchaseOrReturn');

  // Tracks form initialization states during edit mode shifts to prevent accidental baseline blankouts
  const isInitializingRef = useRef(false);

  const purchaseOrReturnOptions = [
    { id: 'Purchase', title: 'Purchase' },
    { id: 'Return', title: 'Return' },
    { id: 'Accommodation', title: 'Accommodation' },
    { id: 'Diesel Purchase', title: 'Diesel Purchase' },
    { id: 'Recharge', title: 'Recharge' },
  ];

  const statusOptions = useMemo(() => {
    if (purchaseOrReturn === 'Purchase') {
      return [
        { id: 'Requested', title: 'Requested' },
        { id: 'Approved', title: 'Approved' },
        { id: 'Cancelled', title: 'Cancelled' },
      ];
    }
    return [
      { id: 'Requested', title: 'Requested' },
      { id: 'Ordered', title: 'Ordered' },
      { id: 'Delivered', title: 'Delivered' },
      { id: 'Approved', title: 'Approved' },
      { id: 'Cancelled', title: 'Cancelled' },
    ];
  }, [purchaseOrReturn]);

  console.log(allUsers);
  const usersAutocompleteData = allUsers.map(user => ({
    label: user.defaultDriverDisplayName || user.displayName,
    value: user.email,
  }));

  const filteredDepartments = useMemo(() => {
    if (!purchaseOrReturn) return [];
    return departmentList
      .filter(d => {
        const types = d.requisitionType?.split('|').map(t => t.trim()) || [];
        return types.includes(purchaseOrReturn);
      })
      .map(d => d.deptNumberDeptNameForReporting);
  }, [departmentList, purchaseOrReturn]);

  const formContextValues = {
    spAutocompleteData: spList,
    usersAutocompleteData,
    machineData: machines,
    statusOptions,
    Type: purchaseTypes,
    WarrantyPeriod: warrenties,
    supplierList,
    departmentList: filteredDepartments,
  };

  // Dialog initialization cycle hook
  useEffect(() => {
    if (!open) return;

    isInitializingRef.current = true;

    if (!isEdit || !selectedPurchaseReq) {
      reset({
        ...methods.formState.defaultValues,
        purchaseOrReturn: 'Purchase',
        requisitionStatus: 'Requested',
      });
      setTimeout(() => {
        isInitializingRef.current = false;
      }, 50);
      return;
    }

    reset({
      ...selectedPurchaseReq,
      purchaseOrReturn: selectedPurchaseReq.purchaseOrReturn ?? 'Purchase',
      requisitionStatus: selectedPurchaseReq.requisitionStatus ?? 'Requested',
      purchaserFeedback: selectedPurchaseReq.purchaserFeedback ?? '',
      feedbackSentTo: selectedPurchaseReq.feedbackSentTo ?? '',
    });

    setTimeout(() => {
      isInitializingRef.current = false;
    }, 50);
  }, [open, isEdit, selectedPurchaseReq, reset]);

  // Automatically sweeps and unsets cross-contamination field keys on Task Type updates
  useEffect(() => {
    if (isInitializingRef.current || !open || isEdit) return;

    // Snaps structural reference of root data layout fields to retain
    const currentSubmissionDate = getValues('submissionDate');
    const currentOriginatedBy = getValues('originatedBy');
    const currentRequisitionStatus = getValues('requisitionStatus');

    // Overwrites conditional field states seamlessly back to baseline default structures
    reset({
      ...DEFAULT,
      purchaseOrReturn: purchaseOrReturn,
      submissionDate: currentSubmissionDate,
      originatedBy: currentOriginatedBy,
      requisitionStatus: currentRequisitionStatus,
    });
  }, [purchaseOrReturn, reset, getValues, open, isEdit]);

  const onSubmit = async values => {
    setIsLoading(true);
    try {
      if (isEdit) {
        await handleRfqEdit(values);
      } else {
        await handlePurchseReqSubmit(values);
      }
    } catch (err) {
      showToast('Failed to save Purchase Requisition', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const onError = errors => {
    const firstKey = Object.keys(errors)[0];
    const msg = errors[firstKey]?.message;
    if (msg) showToast(msg, 'error');
  };

  const handleClose = () => setOpen(false);

  const handleSendFeedback = async () => {
    const feedbackText = getValues('purchaserFeedback');
    const feedbackTo = getValues('feedbackSentTo'); // email address, since usersAutocompleteData uses value: user.email

    if (!feedbackText || !feedbackTo) {
      showToast('Please enter feedback and select a recipient before sending', 'error');
      return;
    }

    setIsSendingFeedback(true);
    try {
      const poRef = selectedPurchaseReq?.poReqNumber;
      const subject = `Purchase Requisition Feedback${poRef ? ` - PO #${poRef}` : ''}`;
      const body = `Feedback on Purchase Requisition${poRef ? ` #${poRef}` : ''}:\n\n"${feedbackText}"\n\nPlease check the system for more details.`;

      const payload = {
        sendTo: feedbackTo,
        notificationSubject: subject,
        notificationBody: body,
        smsBody: null,
        notificationDeliveryType: 'Email',
        flag: 0,
        primaryKey: null,
        templateType: 'PurchaseRequisition',
        rawData: body,
        notificationUniqueId: poRef,
        notificationSection: 'PurchaseRequisition',
      };

      await customAxios.post('/GenTasks/AddNotifications', payload, {
        headers: authHeader(),
      });

      showToast('Feedback sent successfully', 'success');
    } catch (err) {
      console.error('Failed to send feedback notification', err);
      showToast('Failed to send feedback', 'error');
    } finally {
      setIsSendingFeedback(false);
    }
  };

  return (
    <Dialog
      fullWidth
      maxWidth="lg"
      open={open}
      onClose={(event, reason) => {
        // Prevent closing if the user clicks outside or presses Escape
        if (reason && (reason === 'backdropClick' || reason === 'escapeKeyDown')) {
          return;
        }
        setOpen(false);
      }}>
      <DialogTitle sx={{ m: 0, p: 2 }}>{isEdit ? 'Edit Purchase Requisition' : 'Purchase Requisition Form'}</DialogTitle>

      <IconButton
        onClick={handleClose}
        sx={theme => ({ position: 'absolute', right: 8, top: 8, color: theme.palette.grey[500] })}>
        <CloseIcon />
      </IconButton>

      <FormProvider {...methods}>
        <form onSubmit={hookFormSubmit(onSubmit, onError)}>
          <DialogContent dividers>
            <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', mb: 2 }}>
              {selectedPurchaseReq?.poReqNumber && (
                <SummaryCard value={selectedPurchaseReq?.poReqNumber} label="PO Number" />
              )}
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <FormSelect
                    name="purchaseOrReturn"
                    label="Select Requisition Type"
                    options={purchaseOrReturnOptions}
                    disabled={isEdit}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormAutocomplete
                    name="originatedBy"
                    label="Originated By"
                    data={formContextValues?.usersAutocompleteData || []}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormSelect
                    name="requisitionStatus"
                    label="Requisition Status"
                    options={statusOptions}
                    disabled={!isEdit}
                  />
                </Grid>
              </Grid>

              {purchaseOrReturn && <Box sx={{ width: '100%' }}>{SwitchSections(purchaseOrReturn, formContextValues)}</Box>}

              {purchaseOrReturn &&
                (purchaseOrReturn !== 'Diesel Purchase' ||
                  ['Ordered', 'Delivered', 'Approved'].includes(watch('requisitionStatus'))) && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                      File and Invoice Upload
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                      <Box sx={{ flex: 1 }}>
                        <DropzoneUpload
                          label="Upload File"
                          onFileSelect={files => methods.setValue('files', files, { shouldValidate: true })}
                          multiple
                          uploadedFiles={uploadedDocs?.file}
                          onFileDelete={handleFileDelete}
                          onFileView={viewFile}
                        />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <DropzoneUpload
                          label="Upload Invoice"
                          onFileSelect={files => {
                            methods.setValue('invoiceFiles', files, { shouldValidate: true });
                            if (files?.length > 0) {
                              const type = methods.getValues('purchaseOrReturn');
                              const status = methods.getValues('requisitionStatus');
                              const shouldAutoApprove =
                                type === 'Purchase' || (type === 'Diesel Purchase' && ['Ordered', 'Delivered'].includes(status));
                              if (shouldAutoApprove && status !== 'Approved') {
                                methods.setValue('requisitionStatus', 'Approved', { shouldValidate: true });
                                showToast('Status automatically updated to Approved after invoice upload', 'info');
                              }
                            }
                          }}
                          multiple
                          uploadedFiles={uploadedDocs?.invoice}
                          onFileDelete={handleFileDelete}
                          onFileView={viewFile}
                        />
                      </Box>
                    </Box>
                  </Box>
                )}

              {/* NEW FIELDS SECTION: Purchaser Feedback & Feedback Sent To */}
              {isEdit && purchaseOrReturn && (
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                    Feedback Details
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={5}>
                      <FormInput name="purchaserFeedback" label="Purchaser Feedback" fullWidth />
                    </Grid>
                    <Grid item xs={12} sm={5}>
                      <FormAutocomplete
                        name="feedbackSentTo"
                        label="Feedback Send To"
                        data={formContextValues?.usersAutocompleteData || []}
                      />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                      <Button
                        variant="contained"
                        color="success"
                        disabled={isSendingFeedback}
                        onClick={handleSendFeedback}
                      >
                        {isSendingFeedback ? 'Sending...' : 'Send Feedback'}
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              )}

              <Box sx={{ mt: 2 }}>{/* Reserved space preserved */}</Box>
            </Box>
          </DialogContent>

          <DialogActions sx={{ p: 2 }}>
            <Button onClick={handleClose} variant="outlined">
              Cancel
            </Button>
            <Button type="submit" variant="contained" color="primary" disabled={isLoading || !purchaseOrReturn}>
              {isEdit ? 'Save Edit' : 'Save'}
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  );
};

export default AddPurchaseRequisitionForm;
